import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { TextMorph } from 'torph/react';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck, X, Trash2, Loader2, FileText, Calendar, AlertCircle, Newspaper, Tag, Hash, Rss, AtSign } from 'lucide-react';
import { cn } from '@vidnight/ui';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import toast from 'react-hot-toast';
import { http } from '@/network/http';
import { TOPBAR_COLORS, TopBarIconButton } from '@/components/topbar';
import { DetailModal } from '@/components/modal';
import {
  useInfiniteNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
  useDeleteNotification,
  useDeleteAllNotifications,
} from '../hooks';
import { formatRelativeDateTime, replaceMessageDate } from '../utils/relativeDateFormat';
import { localizeNotification } from '../utils/localizeNotification';
import { BellNotificationBubble } from './BellNotificationBubble';
import type { Notification } from '../types';
import { isToday, isYesterday, parseISO } from 'date-fns';

// ─── Bell wiggle keyframes ───

const bellWiggleStyle = `
@keyframes bellWiggle {
  0%   { transform: rotate(0deg); }
  10%  { transform: rotate(12deg); }
  20%  { transform: rotate(-10deg); }
  30%  { transform: rotate(8deg); }
  40%  { transform: rotate(-6deg); }
  60%  { transform: rotate(3deg); }
  80%  { transform: rotate(-1deg); }
  100% { transform: rotate(0deg); }
}
`;

// ─── Helpers ───

function getNotificationIcon(type: Notification['type']) {
  switch (type) {
    case 'REPORT_SUBMITTED':
      return { icon: '📤', color: 'text-cyan-600 bg-cyan-50' };
    case 'REPORT_APPROVED':
      return { icon: '✓', color: 'text-green-600 bg-green-50' };
    case 'REPORT_REJECTED':
      return { icon: '✗', color: 'text-red-600 bg-red-50' };
    case 'MONTHLY_SUMMARY':
      return { icon: '📊', color: 'text-blue-600 bg-blue-50' };
    case 'PAYROLL_GENERATED':
      return { icon: '💰', color: 'text-amber-600 bg-amber-50' };
    case 'ACCOUNT_APPROVED':
      return { icon: '✓', color: 'text-primary-600 bg-primary-50' };
    case 'NEW_USER_PENDING':
      return { icon: '👤', color: 'text-amber-600 bg-amber-50' };
    case 'NEW_MESSAGE':
      return { icon: '💬', color: 'text-blue-600 bg-blue-50' };
    case 'RSS_IMMEDIATE':
      return { icon: '🚨', color: 'text-red-600 bg-red-50' };
    case 'RSS_DAILY_DIGEST':
      return { icon: '📰', color: 'text-orange-600 bg-orange-50' };
    case 'RSS_WEEKLY_DIGEST':
      return { icon: '📋', color: 'text-purple-600 bg-purple-50' };
    default:
      return { icon: '🔔', color: 'text-neutral-600 bg-neutral-50' };
  }
}

function groupNotificationsByDate(
  notifications: Notification[],
  t: (key: string) => string,
): { label: string; items: Notification[] }[] {
  const groups: { label: string; items: Notification[] }[] = [];
  const todayItems: Notification[] = [];
  const yesterdayItems: Notification[] = [];
  const earlierItems: Notification[] = [];

  for (const n of notifications) {
    const date = parseISO(n.createdAt);
    if (isToday(date)) todayItems.push(n);
    else if (isYesterday(date)) yesterdayItems.push(n);
    else earlierItems.push(n);
  }

  if (todayItems.length > 0) groups.push({ label: t('notifications.dateGroups.today'), items: todayItems });
  if (yesterdayItems.length > 0) groups.push({ label: t('notifications.dateGroups.yesterday'), items: yesterdayItems });
  if (earlierItems.length > 0) groups.push({ label: t('notifications.dateGroups.earlier'), items: earlierItems });

  return groups;
}

// ─── Structured message rendering ───

interface NotificationMeta {
  reportId?: string;
  reportType?: string;
  date?: string;
  reason?: string;
  senderName?: string;
  feedName?: string;
  sourceUrl?: string;
  matchedKeyword?: string;
  articleTitle?: string;
  articleCount?: number;
  totalFeeds?: number;
  taggerName?: string;
  messageCount?: number;
  conversationId?: string;
  firstUnreadMessageId?: string;
  messageId?: string;
  pendingUserId?: string;
  userName?: string;
  userEmail?: string;
}

function parseMeta(notification: Notification): NotificationMeta {
  try {
    return notification.metadata ? JSON.parse(notification.metadata) : {};
  } catch {
    return {};
  }
}

function StructuredField({ icon: Icon, value }: { icon: React.ComponentType<{ className?: string }>; value: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span style={{ color: 'var(--user-page-text)' }}><Icon className="h-3.5 w-3.5 flex-shrink-0" /></span>
      <b>{value}</b>
    </span>
  );
}

function renderStructuredMessage(
  notification: Notification,
  meta: NotificationMeta,
  fallbackMessage: string,
  t: (key: string, vars?: Record<string, string | number>) => string,
) {
  switch (notification.type) {
    case 'REPORT_SUBMITTED':
    case 'REPORT_APPROVED': {
      const reportType = meta.reportType ? t(`notifications.reportTypes.${meta.reportType}`) : '';
      const date = meta.date || '';
      if (!reportType && !date) return fallbackMessage;
      return (
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {reportType && <StructuredField icon={FileText} value={reportType} />}
          {date && <StructuredField icon={Calendar} value={date} />}
        </span>
      );
    }
    case 'REPORT_REJECTED': {
      const reportType = meta.reportType ? t(`notifications.reportTypes.${meta.reportType}`) : '';
      const date = meta.date || '';
      const reason = meta.reason || '';
      if (!reportType && !date && !reason) return fallbackMessage;
      return (
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {reportType && <StructuredField icon={FileText} value={reportType} />}
          {date && <StructuredField icon={Calendar} value={date} />}
          {reason && <StructuredField icon={AlertCircle} value={reason} />}
        </span>
      );
    }
    case 'RSS_IMMEDIATE': {
      const title = meta.articleTitle || '';
      const keyword = meta.matchedKeyword || '';
      if (!title && !keyword) return fallbackMessage;
      return (
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {title && <StructuredField icon={Newspaper} value={title} />}
          {keyword && <StructuredField icon={Tag} value={keyword} />}
        </span>
      );
    }
    case 'RSS_DAILY_DIGEST': {
      const count = meta.articleCount;
      if (count == null) return fallbackMessage;
      return (
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <StructuredField icon={Hash} value={String(count)} />
        </span>
      );
    }
    case 'RSS_WEEKLY_DIGEST': {
      const count = meta.articleCount;
      const feeds = meta.totalFeeds;
      if (count == null && feeds == null) return fallbackMessage;
      return (
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {count != null && <StructuredField icon={Hash} value={String(count)} />}
          {feeds != null && <StructuredField icon={Rss} value={String(feeds)} />}
        </span>
      );
    }
    case 'POST_TAGGED': {
      const name = meta.taggerName || '';
      if (!name) return fallbackMessage;
      return (
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <StructuredField icon={AtSign} value={name} />
        </span>
      );
    }
    default:
      return fallbackMessage;
  }
}

// ─── NotificationItem ───

function NotificationItem({
  notification,
  onRead,
  onDelete,
  onClick,
  onApproveUser,
  onRejectUser,
  locale: _locale,
  isMobile,
}: {
  notification: Notification;
  onRead: (id: string) => void;
  onDelete: (id: string) => void;
  onClick: (n: Notification) => void;
  onApproveUser?: (userId: string, notificationId: string) => void;
  onRejectUser?: (userId: string, notificationId: string) => void;
  locale: string;
  isMobile: boolean;
}) {
  const { t } = useTranslation();
  const { icon, color } = getNotificationIcon(notification.type);
  const localized = localizeNotification(notification, t);
  const meta = parseMeta(notification);
  const messageCount = notification.type === 'NEW_MESSAGE' ? (meta.messageCount || 1) : 0;

  return (
    <div
      data-notification-id={notification.id}
      data-unread={!notification.isRead ? 'true' : undefined}
      className="dock-icon-glass dock-glass-subtle group relative flex w-full items-start gap-3 p-3 mb-1.5 text-start cursor-pointer"
      style={{
        borderRadius: 16,
      }}
      onClick={() => onClick(notification)}
    >
      <span
        className={cn(
          'relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-base',
          color
        )}
      >
        {icon}
        {/* Message count badge */}
        {messageCount > 1 && (
          <span
            className="absolute -top-1.5 -end-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[8px] font-bold text-white"
          >
            {messageCount > 99 ? '99' : messageCount}
          </span>
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'text-[15px] leading-tight',
            !notification.isRead ? 'font-bold' : 'font-medium'
          )}
          style={{ color: notification.isRead ? 'var(--user-page-text)' : 'var(--user-page-heading)' }}
        >
          {localized.title}
        </p>
        <div
          className="mt-1 text-sm leading-relaxed line-clamp-2"
          style={{ color: 'var(--user-page-text)' }}
        >
          {renderStructuredMessage(notification, meta, replaceMessageDate(localized.message), t)}
        </div>
        <p className="mt-1.5 text-[11px] font-medium uppercase tracking-wide" style={{ color: 'var(--user-page-text)' }}>
          {formatRelativeDateTime(notification.createdAt)}
        </p>

        {/* Quick actions for pending user approval */}
        {notification.type === 'NEW_USER_PENDING' && meta.pendingUserId && (
          <div
            className="flex gap-2 mt-2 relative z-10"
            ref={(el) => {
              if (el && !el.dataset.pdGuard) {
                el.dataset.pdGuard = '1';
                // Native listener stops pointerdown from reaching motion's
                // drag handler on the parent BaseModal motion.div
                el.addEventListener('pointerdown', (ev) => ev.stopPropagation());
              }
            }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onApproveUser?.(meta.pendingUserId!, notification.id);
              }}
              className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase transition-all active:scale-[0.97]"
              style={{ color: '#FFFFFF', backgroundColor: '#1877F2', touchAction: 'manipulation' }}
            >
              {t('userApprovals.approve')}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRejectUser?.(meta.pendingUserId!, notification.id);
              }}
              className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase transition-all active:scale-[0.97]"
              style={{ color: '#65676B', backgroundColor: '#F0F2F5', touchAction: 'manipulation' }}
            >
              {t('userApprovals.reject')}
            </button>
          </div>
        )}
      </div>

      {/* Unread dot */}
      {!notification.isRead && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigator.vibrate?.(15);
            onRead(notification.id);
          }}
          className="mt-1 h-3 w-3 shrink-0 rounded-full"
          style={{ backgroundColor: TOPBAR_COLORS.unread, border: '1px solid rgba(148, 163, 184, 0.3)' }}
          title={t('notifications.markRead')}
        />
      )}

      {/* Delete button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete(notification.id);
        }}
        className={cn(
          'absolute top-2.5 ltr:right-2.5 rtl:left-2.5 rounded-md p-1.5 transition-all',
          isMobile ? 'hover:text-red-500' : 'opacity-0 group-hover:opacity-100 hover:text-red-500'
        )}
        style={{ color: 'var(--user-page-text)' }}
        title={t('notifications.delete')}
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

// ─── NotificationBell ───

interface NotificationBellProps {
  /** Custom trigger renderer — when provided, replaces the default TopBarIconButton */
  renderTrigger?: (props: { onClick: () => void; unreadCount: number }) => React.ReactNode;
}

export function NotificationBell({ renderTrigger }: NotificationBellProps = {}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const [open, setOpen] = useState(false);
  const [isWiggling, setIsWiggling] = useState(false);
  const [bubbleMessage, setBubbleMessage] = useState<string | null>(null);
  const bellRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const mobileSentinelRef = useRef<HTMLDivElement>(null);
  const seenIdsRef = useRef<Set<string>>(new Set());
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoReadObserverRef = useRef<IntersectionObserver | null>(null);

  const { data: unreadData } = useUnreadCount();
  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteNotifications();
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  const deleteNotification = useDeleteNotification();
  const deleteAll = useDeleteAllNotifications();

  const unreadCount = unreadData?.data?.count ?? 0;
  const notifications = useMemo(
    () => infiniteData?.pages.flatMap((p) => p.data) ?? [],
    [infiniteData]
  );

  // ─── Close on outside click ───
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        bellRef.current &&
        !bellRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // ─── Close on escape ───
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  // ─── IntersectionObserver sentinel for infinite scroll ───
  useEffect(() => {
    if (!open) return;
    const targets = [sentinelRef.current, mobileSentinelRef.current].filter(Boolean) as HTMLElement[];
    if (targets.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // ─── Auto-mark-as-read on scroll (IntersectionObserver) ───
  const flushSeenIds = useCallback(async () => {
    const ids = Array.from(seenIdsRef.current);
    if (ids.length === 0) return;
    seenIdsRef.current.clear();

    navigator.vibrate?.(15);

    await Promise.allSettled(
      ids.map((id) => http.patch(`/notifications/${id}/read`))
    );
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }, [queryClient]);

  const queueMarkSeen = useCallback(
    (id: string) => {
      seenIdsRef.current.add(id);
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(flushSeenIds, 800);
    },
    [flushSeenIds]
  );

  useEffect(() => {
    if (!open) {
      autoReadObserverRef.current?.disconnect();
      return;
    }

    const scrollContainer = isMobile ? mobileScrollRef.current : scrollRef.current;
    if (!scrollContainer) return;

    autoReadObserverRef.current?.disconnect();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            const id = el.dataset.notificationId;
            const isUnread = el.dataset.unread === 'true';
            if (id && isUnread) {
              queueMarkSeen(id);
            }
          }
        }
      },
      { threshold: 0.6, root: scrollContainer }
    );

    autoReadObserverRef.current = observer;

    const items = scrollContainer.querySelectorAll('[data-notification-id]');
    items.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [open, isMobile, notifications, queueMarkSeen]);

  // Cleanup flush timer on unmount
  useEffect(() => {
    return () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    };
  }, []);

  // ─── Bell wiggle + bubble on report-submitted event ───
  // Multiple NotificationBell instances may be mounted (nav rail + admin header).
  // All bells wiggle, but only the first handler to fire shows the bubble.
  useEffect(() => {
    const handleReportSubmitted = (e: Event) => {
      setIsWiggling(true);
      setTimeout(() => setIsWiggling(false), 800);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });

      // Only one bell shows the bubble — first handler claims it
      const evt = e as CustomEvent;
      if (!evt.detail?.bubbleClaimed) {
        if (!evt.detail) (evt as any).detail = {};
        evt.detail.bubbleClaimed = true;
        setBubbleMessage(t('notifications.reportSubmitted'));
      }
    };

    window.addEventListener('report-submitted', handleReportSubmitted);
    return () => window.removeEventListener('report-submitted', handleReportSubmitted);
  }, [t, queryClient]);

  // ─── Click to navigate ───
  const handleNotificationClick = useCallback(
    (n: Notification) => {
      if (!n.isRead) markAsRead.mutate(n.id);
      setOpen(false);

      try {
        if (n.metadata) {
          const meta = JSON.parse(n.metadata);
          if (n.type === 'NEW_MESSAGE' && meta.conversationId) {
            const scrollTo = meta.firstUnreadMessageId || meta.messageId;
            const url = scrollTo
              ? `/messages/${meta.conversationId}?scrollTo=${scrollTo}`
              : `/messages/${meta.conversationId}`;
            navigate(url);
            // Nudge Stackflow's historySyncPlugin to re-read the URL
            requestAnimationFrame(() => {
              window.dispatchEvent(new PopStateEvent('popstate'));
            });
            return;
          }
          if (['RSS_IMMEDIATE', 'RSS_DAILY_DIGEST', 'RSS_WEEKLY_DIGEST'].includes(n.type)) {
            if (meta.sourceUrl) { window.open(meta.sourceUrl, '_blank'); return; }
            navigate('/feed');
            return;
          }
          if (n.type === 'POST_TAGGED') {
            navigate('/feed');
            return;
          }
          if (n.type === 'NEW_USER_PENDING') {
            navigate('/admin/user-approvals');
            return;
          }
          if (n.type === 'ACCOUNT_APPROVED') {
            navigate('/dashboard');
            return;
          }
          if (
            ['REPORT_SUBMITTED', 'REPORT_APPROVED', 'REPORT_REJECTED'].includes(n.type) &&
            meta.reportId
          ) {
            navigate(`/reports?report=${meta.reportId}`);
            return;
          }
        }
      } catch {
        // metadata not parseable, ignore
      }
    },
    [markAsRead, navigate]
  );

  const handleRead = (id: string) => {
    markAsRead.mutate(id);
  };

  const handleDelete = (id: string) => {
    deleteNotification.mutate(id);
  };

  const handleApproveUser = (userId: string, notificationId: string) => {
    markAsRead.mutate(notificationId);
    setOpen(false);
    navigate(`/admin/user-approvals?userId=${userId}`);
  };

  const handleRejectUser = async (userId: string, notificationId: string) => {
    if (!window.confirm(t('userApprovals.rejectConfirmMessage'))) return;
    try {
      await http.post(`/users/${userId}/reject`);
      markAsRead.mutate(notificationId);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success(t('userApprovals.rejected'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleMarkAllRead = () => {
    markAllAsRead.mutate();
  };

  const handleDeleteAll = () => {
    if (window.confirm(t('notifications.confirmDeleteAll'))) {
      deleteAll.mutate();
    }
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate('/notifications');
  };

  // Position the dropdown relative to the bell button
  const getDropdownPosition = () => {
    if (!bellRef.current) return {};
    const rect = bellRef.current.getBoundingClientRect();
    const top = rect.bottom + 8;
    const dropdownWidth = 380;
    // Prefer left-aligned with the bell; clamp so it stays on-screen
    const left = Math.min(
      Math.max(8, rect.left),
      window.innerWidth - dropdownWidth - 8,
    );
    return {
      position: 'fixed' as const,
      top,
      left,
      maxHeight: `calc(100dvh - ${top + 16}px)`,
      zIndex: 9999,
    };
  };

  const dateGroups = useMemo(
    () => groupNotificationsByDate(notifications, t),
    [notifications, t]
  );

  // ─── Shared notification list renderer ───
  const renderNotificationList = (ref: React.RefObject<HTMLDivElement | null>, sentRef: React.RefObject<HTMLDivElement | null>, containerClass = 'flex-1 min-h-0 overflow-y-auto p-2') => (
    <div ref={ref} className={containerClass}>
      {notifications.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-12">
          <Bell className="h-8 w-8" style={{ color: '#1E293B30' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--user-page-text)' }}>{t('notifications.empty')}</p>
        </div>
      ) : (
        <>
          {dateGroups.map((group) => (
            <div key={group.label}>
              {dateGroups.length > 1 && (
                <div
                  className="sticky top-0 z-10 px-3 py-2 text-[11px] font-bold uppercase tracking-widest backdrop-blur-sm"
                  style={{ color: 'var(--user-page-text)', backgroundColor: 'var(--user-page-card-bg)' }}
                >
                  {group.label}
                </div>
              )}
              {group.items.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onRead={handleRead}
                  onDelete={handleDelete}
                  onClick={handleNotificationClick}
                  onApproveUser={handleApproveUser}
                  onRejectUser={handleRejectUser}
                  locale={i18n.language}
                  isMobile={isMobile}
                />
              ))}
            </div>
          ))}
          {/* Sentinel for infinite scroll */}
          <div ref={sentRef} className="h-1" />
        </>
      )}
      {isFetchingNextPage && (
        <div className="flex items-center justify-center gap-2 py-3">
          <Loader2 className="h-4 w-4 animate-spin" style={{ color: 'var(--user-page-text)' }} />
          <span className="text-xs" style={{ color: 'var(--user-page-text)' }}>
            {t('common.loading')}
          </span>
        </div>
      )}
    </div>
  );

  // ─── Shared footer ───
  const renderFooter = (extraClass?: string) => (
    notifications.length > 0 ? (
      <div
        className={cn('px-3 py-3 flex gap-2 flex-shrink-0', extraClass)}
        style={{
          borderTop: '1px solid rgba(255,255,255,0.15)',
          background: 'transparent',
        }}
      >
        <button
          onClick={handleViewAll}
          className="dock-icon-glass dock-glass-subtle flex-1 py-2.5 text-center text-sm font-bold uppercase transition-all"
          style={{ borderRadius: 16, color: '#1a4fa0' }}
        >
          {t('notifications.viewAll')}
        </button>
        <button
          onClick={handleDeleteAll}
          className="dock-icon-glass dock-glass-subtle py-2.5 px-3 text-center text-sm font-bold uppercase transition-all"
          style={{ borderRadius: 16, color: '#1a4fa0' }}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    ) : null
  );

  return (
    <>
      <style>{bellWiggleStyle}</style>

      {/* Bell button */}
      <div ref={bellRef} data-notification-bell className="flex">
        {renderTrigger ? (
          renderTrigger({ onClick: () => setOpen(!open), unreadCount })
        ) : (
          <TopBarIconButton
            onClick={() => setOpen(!open)}
            aria-label={t('nav.notifications')}
            active={open}
            badge={
              unreadCount > 0 ? (
                <span
                  className="absolute flex items-center justify-center rounded-full bg-red-500 text-[7px] font-bold text-white"
                  style={{
                    top: '-4px',
                    right: '-4px',
                    width: '20px',
                    height: '20px',
                    border: `1px solid ${TOPBAR_COLORS.border}`,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }}
                >
                  <TextMorph as="span" duration={300} locale="he" respectReducedMotion>
                    {unreadCount > 99 ? '99' : String(unreadCount)}
                  </TextMorph>
                </span>
              ) : undefined
            }
          >
            <Bell
              className="h-7 w-7"
              style={isWiggling ? { animation: 'bellWiggle 0.8s ease-in-out' } : undefined}
            />
          </TopBarIconButton>
        )}
      </div>

      {/* Bubble toast */}
      {bubbleMessage && (
        <BellNotificationBubble
          message={bubbleMessage}
          onClose={() => setBubbleMessage(null)}
          bellRef={bellRef}
          isMobile={isMobile}
        />
      )}

      {/* Desktop dropdown portal */}
      {!isMobile && createPortal(
        <AnimatePresence>
          {open && (
              <motion.div
                key="desktop-dropdown"
                ref={dropdownRef}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                style={{
                  ...getDropdownPosition(),
                  border: `1px solid ${TOPBAR_COLORS.border}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
                className="glass-surface-modal w-[380px] overflow-hidden flex flex-col"
              >
                {/* Header — blue gradient matching mobile */}
                <div
                  className="flex items-center justify-between px-4 py-3 flex-shrink-0"
                  style={{
                    background: 'linear-gradient(180deg, #0d2b6b 0%, #1a4fa0 40%, #3b8dd4 75%, #6bb4e8 100%)',
                    borderBottom: 'none',
                  }}
                >
                  <h3 className="text-sm font-bold uppercase text-white">
                    {t('notifications.title')}
                  </h3>
                  <div className="flex items-center gap-1">
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        className="rounded-lg p-1.5 transition-all hover:bg-white/20"
                        style={{
                          color: '#ffffff',
                          background: 'rgba(255,255,255,0.18)',
                          backdropFilter: 'blur(12px)',
                          WebkitBackdropFilter: 'blur(12px)',
                          border: '1px solid rgba(255,255,255,0.25)',
                          borderRadius: 10,
                        }}
                        title={t('notifications.markAllRead')}
                      >
                        <CheckCheck className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => setOpen(false)}
                      className="rounded-lg p-1.5 transition-all hover:bg-white/20"
                      style={{
                        color: '#ffffff',
                        background: 'rgba(255,255,255,0.18)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255,255,255,0.25)',
                        borderRadius: 10,
                      }}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {renderNotificationList(scrollRef, sentinelRef)}
                {renderFooter()}
              </motion.div>

          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Mobile fullscreen notification modal */}
      {isMobile && (
        <DetailModal isOpen={open} onClose={() => setOpen(false)} ariaLabel={t('notifications.title')} flushTop>
          <div className="flex flex-col flex-1 min-h-0">
            {/* Header — frosted glass */}
            <div
              className="flex-shrink-0"
              style={{
                backgroundColor: 'rgba(255,255,255,0.55)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                paddingTop: 'env(safe-area-inset-top, 0px)',
                borderBottom: '2px solid rgba(255,255,255,0.25)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            >
              {/* Drag handle */}
              <div className="flex justify-center pt-2 pb-1">
                <div
                  className="w-12 h-1.5 rounded-full"
                  style={{ backgroundColor: 'rgba(0,0,0,0.15)' }}
                />
              </div>
              <div className="flex items-center justify-center relative px-4 py-3">
                <h1
                  style={{
                    color: '#1e293b',
                    fontSize: 18,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                  }}
                >
                  {t('notifications.title')}
                </h1>
                <div className="absolute flex items-center gap-1" style={{ right: 16 }}>
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllRead}
                      className="p-2 rounded-full transition-colors hover:bg-black/5"
                      style={{ color: '#1e293b' }}
                      title={t('notifications.markAllRead')}
                    >
                      <CheckCheck className="h-5 w-5" />
                    </button>
                  )}
                  <button
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-center rounded-full transition-colors cursor-pointer"
                    style={{
                      width: 32,
                      height: 32,
                      backgroundColor: 'rgba(0,0,0,0.05)',
                      border: '1px solid rgba(0,0,0,0.08)',
                    }}
                    aria-label={t('common.close')}
                  >
                    <X className="w-4 h-4" style={{ color: '#1e293b' }} />
                  </button>
                </div>
              </div>
            </div>

            {/* Notification list */}
            <div
              className="flex-1 overflow-y-auto"
              style={{
                backgroundColor: 'rgba(255,255,255,0.55)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              }}
            >
              {renderNotificationList(mobileScrollRef, mobileSentinelRef, 'p-2')}
            </div>

            {/* Footer */}
            <div
              style={{
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
                backgroundColor: 'rgba(255,255,255,0.55)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              }}
            >
              {renderFooter()}
            </div>
          </div>
        </DetailModal>
      )}
    </>
  );
}
